import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import FileResourceForm from '@components/forms/FileResourceForm';
import Modal from '@components/Modal';
import Popover from '@components/popovers/Popover';
import PopoverItem from '@components/popovers/PopoverItem';
import Icon from '@components/text/Icon';
import useDestroyFileResource from '@hooks/mutations/useDestroyFileResource';
import useFileResourceDownloader from '@hooks/useFileResourceDownloader';
import useOpener from '@hooks/useOpener';
import {
  FileResourceFragment,
  GetAttachmentDocument,
  GetAttachmentQuery,
  GetAttachmentQueryVariables,
  GetJourneyWithDocumentsQuery
} from '@typing/Generated';

type Props = {
  fileResource: FileResourceFragment;
  journey: GetJourneyWithDocumentsQuery['journey'];
};

const DocumentPopover = ({ fileResource, journey }: Props) => {
  const [documentEditKind, setDocumentEditKind] = useState<'folder' | 'name'>('name');
  const fileResourceOpener = useOpener();
  const intl = useIntl();
  const destroyFileResource = useDestroyFileResource(journey);
  const fileResourceDownloader = useFileResourceDownloader();

  const opener = useOpener();

  const handleDownload = useCallback(() => {
    fileResourceDownloader.handleDownload(fileResource, journey.id);
  }, [fileResource, fileResourceDownloader, journey.id]);

  const getAttachmentQuery = useQuery<GetAttachmentQuery, GetAttachmentQueryVariables>(GetAttachmentDocument, {
    skip: !fileResource.attachment || fileResource.attachment.scanning === false,
    variables: { id: fileResource.attachment?.id ?? '' }
  });

  useEffect(() => {
    if (fileResource.attachment && fileResource.attachment.scanning) {
      getAttachmentQuery.startPolling(2500);
    } else {
      getAttachmentQuery.stopPolling();
    }

    return () => {
      getAttachmentQuery.stopPolling();
    };
  }, [fileResource.attachment, getAttachmentQuery]);

  return (
    <div>
      {fileResource.attachment?.scanning ? (
        <Icon icon="spinner" />
      ) : (
        <Popover isOpen={opener.isOpen} opener={opener}>
          <PopoverItem icon="download" onClick={handleDownload} textKey="pages.drive.download" />
          <PopoverItem
            icon="gear"
            onClick={() => {
              setDocumentEditKind('name');
              fileResourceOpener.open();
            }}
            textKey="pages.drive.rename"
          />
          <PopoverItem
            icon="paperPlane"
            onClick={() => {
              setDocumentEditKind('folder');
              fileResourceOpener.open();
            }}
            textKey="pages.drive.move"
          />

          <PopoverItem
            icon="trash"
            noBorder
            onClick={() => {
              destroyFileResource({ id: fileResource.id });
              opener.close();
            }}
            textKey="pages.drive.delete"
          />
        </Popover>
      )}

      <Modal
        opener={fileResourceOpener}
        title={intl.formatMessage({
          id: 'pages.documents.editFileResource'
        })}
      >
        {({ modalDisplayed }) => (
          <FileResourceForm
            autofocus={modalDisplayed}
            editKind={documentEditKind}
            fileResource={fileResource}
            journey={journey}
            onCancel={fileResourceOpener.close}
            onSuccess={() => {
              fileResourceOpener.close();
              opener.close();
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default DocumentPopover;
